import { useMemo, useState } from 'react';

import { IconEdit, IconPrinter } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmUpdate } from '../../lib/confirm-service';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { useOpdPatientBills, useUpdateOpdRegistrationPayment } from './opd.queries';
import { currency, formatOpdDateTime } from './opd.utils';
import type { OpdPayment, OpdPaymentMode, OpdRegistration } from './types';

const paymentModes: OpdPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

interface MergedReceipt {
  key: string;
  billType: 'OPD';
  billId: string;
  paymentIndex: number;
  billNumber: string;
  date: string;
  mode: string;
  bankName: string | null;
  receivedBy: string;
  amount: number;
}

export const OpdAllReceiptList = ({ row }: { row: OpdRegistration }) => {
  const uhid = row.patientSnapshot.uhid;
  const opdQuery = useOpdPatientBills(uhid);
  const [editing, setEditing] = useState<{ billId: string; paymentIndex: number; payment: OpdPayment } | null>(null);

  const receipts = useMemo<MergedReceipt[]>(() => {
    const opdReceipts: MergedReceipt[] = (opdQuery.data?.data ?? []).flatMap((bill) => bill.payments.map((payment, index) => ({
      key: `opd-${bill.id}-${index}`,
      billType: 'OPD' as const,
      billId: bill.id,
      paymentIndex: index,
      billNumber: bill.billing.billNumber,
      date: bill.appointmentDate,
      mode: payment.mode,
      bankName: payment.bankName,
      receivedBy: bill.generatedBy.name,
      amount: payment.amount,
    })));
    return opdReceipts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [opdQuery.data]);
  const tableSort = useTableSort(receipts, {
    receiptNo: (receipt) => `R${receipt.billNumber}`,
    date: (receipt) => receipt.date,
    mode: (receipt) => receipt.mode,
    bank: (receipt) => receipt.bankName,
    receivedBy: (receipt) => receipt.receivedBy,
    amount: (receipt) => receipt.amount,
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their receipt history can't be looked up." />;
  if (opdQuery.isLoading) return <LoadingState />;
  if (opdQuery.isError) return <ErrorState title="Unable to load receipts" message={opdQuery.error instanceof ApiError ? opdQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void opdQuery.refetch() }} />;

  const totalReceived = receipts.reduce((sum, receipt) => sum + receipt.amount, 0);

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>All Receipt — {row.patientSnapshot.name}</span></div>
        <p>UHID {uhid} · {receipts.length} receipt{receipts.length === 1 ? '' : 's'}</p>
      </header>
      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Receipts across all bills</h3><span>Total Received <strong>{currency(totalReceived)}</strong></span></div>
        {receipts.length === 0 ? (
          <p className="opd-bill-empty-note">No payments have been recorded for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="receiptNo">Receipt No</SortableTh><SortableTh control={tableSort} column="date">Date</SortableTh><SortableTh control={tableSort} column="mode">Mode</SortableTh><SortableTh control={tableSort} column="bank">Bank</SortableTh><SortableTh control={tableSort} column="receivedBy">Received By</SortableTh><SortableTh control={tableSort} column="amount">Amount</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {tableSort.rows.map((receipt, index) => (
                  <tr key={receipt.key}>
                    <td>{index + 1}</td>
                    <td>R{receipt.billNumber} <span className={`opd-badge ${receipt.billType === 'OPD' ? 'patient-type' : 'rescheduled'}`}>{receipt.billType}</span></td>
                    <td>{formatOpdDateTime(receipt.date)}</td>
                    <td>{receipt.mode}</td>
                    <td>{receipt.bankName ?? '-'}</td>
                    <td>{receipt.receivedBy}</td>
                    <td>{currency(receipt.amount)}</td>
                    <td className="opd-row-action-group">
                      {receipt.billType === 'OPD' ? (
                        <>
                          <button
                            type="button"
                            className="opd-row-action-icon"
                            title="Print Receipt"
                            aria-label="Print Receipt"
                            onClick={() => openAuthenticatedRoute(`/opd/${receipt.billId}/print-receipt/${receipt.paymentIndex}`)}
                          >
                            <IconPrinter />
                          </button>
                          <button
                            type="button"
                            className="opd-row-action-icon"
                            title="Edit Receipt"
                            aria-label="Edit Receipt"
                            onClick={() => setEditing({ billId: receipt.billId, paymentIndex: receipt.paymentIndex, payment: { mode: receipt.mode as OpdPaymentMode, bankName: receipt.bankName, amount: receipt.amount } })}
                          >
                            <IconEdit />
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <OpdEditReceiptModal editing={editing} onClose={() => setEditing(null)} />
    </>
  );
};

const OpdEditReceiptModal = ({ editing, onClose }: { editing: { billId: string; paymentIndex: number; payment: OpdPayment } | null; onClose: () => void }) => {
  const [mode, setMode] = useState<OpdPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [seed, setSeed] = useState(editing);
  if (editing !== seed) {
    setSeed(editing);
    if (editing) {
      setMode(editing.payment.mode);
      setBankName(editing.payment.bankName ?? '');
      setAmount(editing.payment.amount);
    }
  }
  const mutation = useUpdateOpdRegistrationPayment();
  const { showToast } = useToast();

  const submit = () => {
    if (!editing || amount <= 0) return;
    void confirmUpdate('this receipt').then((confirmed) => {
      if (!confirmed) return;
      mutation.mutate(
        { id: editing.billId, paymentIndex: editing.paymentIndex, payload: { mode, bankName: bankName.trim() || null, amount } },
        {
          onSuccess: () => {
            showToast('Receipt saved successfully.', 'success');
            onClose();
          },
          onError: (error) => showToast(error instanceof ApiError ? error.message : 'The receipt could not be updated.', 'error'),
        },
      );
    });
  };

  return (
    <Modal open={editing !== null} onClose={() => { if (!mutation.isPending) onClose(); }} labelledBy="edit-opd-receipt-title">
      <header>
        <div><p>Receipt</p><h2 id="edit-opd-receipt-title">Edit Receipt</h2></div>
        <button type="button" onClick={onClose} disabled={mutation.isPending} aria-label="Close">×</button>
      </header>
      <form onSubmit={(event) => { event.preventDefault(); submit(); }}>
        <label className="field"><span>Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as OpdPaymentMode)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field"><span>Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
        <label className="field"><span>Amount</span><input required type="number" min={0.01} step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
        <div className="bed-modal-actions">
          <button className="button button-secondary" type="button" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button className="button button-primary" type="submit" disabled={amount <= 0 || mutation.isPending}>{mutation.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};
